'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { Fragment } from 'react';

import { Link } from '@natu/next-link';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { useDraftModeContext } from '../../../../../../storyblok-preview';
import { getArticlesByFilters } from '../../../../utils/getArticlesByFilters';
import { ArticlesByFiltersContentProps } from '../types';
import ArticlesGrid from './ArticlesGrid';

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

const RecommendedArticles = dynamic(() =>
  import('./RecommendedArticles').then(mod => mod.RecommendedArticles),
);

const Pagination = dynamic(() => import('@natu/ui/Pagination').then(mod => mod.Pagination));

export const ArticlesByFiltersContent = ({
  author,
  category,
  resolvedAllPostsLink,
  allPostsLabel,
  heading,
  recommendedArticles,
  withPagination,
  page = 1,
}: ArticlesByFiltersContentProps) => {
  const { draftMode } = useDraftModeContext();

  const { data } = useSuspenseQuery({
    queryKey: ['articles', { filters: { author, category }, page, draft: draftMode }],
    queryFn: async () =>
      getArticlesByFilters({ filters: { author, category }, page, draftMode: draftMode }),
  });

  const { articles, articlesPerPage, total } = data;

  if (!isArrayWithLength(articles.stories)) return null;

  const firstRowArticles = articles?.stories?.slice(0, 3);
  const remainingArticles = articles?.stories?.slice(3);

  const pages = Math.ceil(total / articlesPerPage);

  return (
    <Fragment>
      {heading && (
        <div className="mb-10 flex flex-col gap-y-4 lg:flex-row lg:items-end lg:justify-between lg:gap-x-4">
          <h2 className="text-display-sm-28 xl:text-display-md-34 font-semibold">{heading}</h2>
          {allPostsLabel && resolvedAllPostsLink && (
            <Link
              {...resolvedAllPostsLink}
              className="text-primary-600 flex items-center gap-x-2 text-[0.813rem] font-semibold uppercase leading-[1.5rem] tracking-[-0.2px] underline-offset-4 hover:underline"
            >
              {allPostsLabel}
              <DynamicIcon icon="arrowRight" />
            </Link>
          )}
        </div>
      )}
      <div className="flex flex-col gap-y-[4.5rem] lg:gap-y-16">
        <ArticlesGrid id="first-row" articles={firstRowArticles} />
        {page === 1 && <RecommendedArticles recommendedArticles={recommendedArticles} />}
        <ArticlesGrid id="second-row" articles={remainingArticles} />
      </div>
      {pages > 1 && withPagination && (
        <Pagination className="mt-14 md:mt-20" pages={pages} activePage={page} />
      )}
    </Fragment>
  );
};
